import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiFileText, FiPlay, FiEdit2 } from 'react-icons/fi';
import Header from '../components/layout/Header';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Avatar from '../components/ui/Avatar';
import Tabs from '../components/ui/Tabs';
import { Employee } from '../types';
import { employeesApi } from '../api/employees';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale/fr';

const EmployeeDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await employeesApi.get(id!);
        const d = res.data as any;
        setEmployee(d.employee || d);
      } catch {
        setEmployee({
          id: id || '1', company_id: '1', first_name: 'Marie', last_name: 'Dupont',
          email: 'marie.dupont@entreprise.fr', position: 'Directrice RH', department: 'Ressources Humaines',
          contract_type: 'CDI', hire_date: '2020-03-15', status: 'active',
          phone: '+33 6 12 34 56 78', address: '15 rue de la Paix, 75002 Paris',
          birth_date: '1985-07-22', created_at: '2020-03-15', updated_at: '2026-01-10',
        });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading || !employee) {
    return (
      <div>
        <Header title="Chargement..." />
        <div style={{ padding: '2rem', display: 'flex', justifyContent: 'center' }}>
          <div className="skeleton" style={{ width: '100%', maxWidth: 800, height: 400 }} />
        </div>
      </div>
    );
  }

  const fullName = `${employee.first_name} ${employee.last_name}`;

  const tabs = [
    { key: 'info', label: 'Informations' },
    { key: 'contract', label: 'Contrat' },
    { key: 'documents', label: 'Documents' },
    { key: 'history', label: 'Historique' },
  ];

  const infoRows: { label: string; value: string }[] = [
    { label: 'E-mail', value: employee.email },
    { label: 'Téléphone', value: employee.phone || '-' },
    { label: 'Adresse', value: employee.address || '-' },
    { label: 'Date de naissance', value: employee.birth_date ? format(new Date(employee.birth_date), 'd MMMM yyyy', { locale: fr }) : '-' },
    { label: 'Département', value: employee.department },
    { label: 'Poste', value: employee.position },
  ];

  const contractRows: { label: string; value: string }[] = [
    { label: 'Type de contrat', value: employee.contract_type },
    { label: "Date d'embauche", value: format(new Date(employee.hire_date), 'd MMMM yyyy', { locale: fr }) },
    { label: 'Date de fin', value: employee.end_date ? format(new Date(employee.end_date), 'd MMMM yyyy', { locale: fr }) : 'Non définie' },
    { label: 'Statut', value: employee.status === 'active' ? 'Actif' : employee.status === 'inactive' ? 'Inactif' : 'En attente' },
  ];

  const timelineEvents = [
    { date: '2026-01-10', title: 'Mise à jour du profil', type: 'edit' },
    { date: '2025-06-15', title: 'Entretien annuel réalisé', type: 'review' },
    { date: '2024-03-15', title: 'Anniversaire 4 ans', type: 'milestone' },
    { date: '2023-09-01', title: 'Promotion - Directrice RH', type: 'promotion' },
    { date: '2020-03-15', title: 'Embauche', type: 'hire' },
  ];

  return (
    <div>
      <Header title="Détail du salarié" />

      <div style={{ padding: '1.5rem 2rem' }}>
        <Button variant="ghost" icon={<FiArrowLeft size={16} />} onClick={() => navigate('/employees')} style={{ marginBottom: '1rem' }}>
          Retour
        </Button>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '1.5rem' }}>
          {/* Main content */}
          <div>
            {/* Employee header card */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <Card style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                  <Avatar name={fullName} size={72} />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
                      <h2 style={{ margin: 0 }}>{fullName}</h2>
                      <Badge variant={employee.status === 'active' ? 'success' : 'danger'}>
                        {employee.status === 'active' ? 'Actif' : 'Inactif'}
                      </Badge>
                    </div>
                    <p style={{ color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>
                      {employee.position} - {employee.department}
                    </p>
                    <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
                      {employee.contract_type} depuis {format(new Date(employee.hire_date), 'MMMM yyyy', { locale: fr })}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <Button variant="outline" icon={<FiEdit2 size={14} />} size="sm" onClick={() => setActiveTab('info')}>Modifier</Button>
                    <Button variant="outline" icon={<FiFileText size={14} />} size="sm" onClick={() => navigate('/documents')}>Document</Button>
                    <Button icon={<FiPlay size={14} />} size="sm" onClick={() => navigate('/obligations')}>Procédure</Button>
                  </div>
                </div>
              </Card>
            </motion.div>

            {/* Tabs content */}
            <Card>
              <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

              {activeTab === 'info' && (
                <div>
                  {infoRows.map((row, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        padding: '0.875rem 0',
                        borderBottom: i < infoRows.length - 1 ? '1px solid var(--color-border-light)' : 'none',
                      }}
                    >
                      <span style={{ width: 180, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', fontWeight: 500 }}>
                        {row.label}
                      </span>
                      <span style={{ fontSize: 'var(--font-size-sm)' }}>{row.value}</span>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'contract' && (
                <div>
                  {contractRows.map((row, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        padding: '0.875rem 0',
                        borderBottom: i < contractRows.length - 1 ? '1px solid var(--color-border-light)' : 'none',
                      }}
                    >
                      <span style={{ width: 180, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', fontWeight: 500 }}>
                        {row.label}
                      </span>
                      <span style={{ fontSize: 'var(--font-size-sm)' }}>{row.value}</span>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'documents' && (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>
                  Aucun document associé
                </div>
              )}

              {activeTab === 'history' && (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>
                  Historique des actions
                </div>
              )}
            </Card>
          </div>

          {/* Right sidebar - Timeline */}
          <div>
            <Card header={<span>Chronologie</span>}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {timelineEvents.map((event, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      gap: '0.75rem',
                      padding: '0.75rem 0',
                      borderBottom: i < timelineEvents.length - 1 ? '1px solid var(--color-border-light)' : 'none',
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: i === 0 ? 'var(--color-accent)' : 'var(--color-border)',
                          marginTop: 4,
                        }}
                      />
                      {i < timelineEvents.length - 1 && (
                        <div style={{ width: 1, flex: 1, background: 'var(--color-border-light)', marginTop: 4 }} />
                      )}
                    </div>
                    <div>
                      <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500 }}>{event.title}</p>
                      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                        {format(new Date(event.date), 'd MMM yyyy', { locale: fr })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeDetailPage;
